import MojitoCore from 'mojito/core';

import {mergeTo} from '#core/utils/config-utils.js';
import {countAndIncreaseAutomatedPropertiesCount} from '#core/utils/statistics.js';
import {isMobile} from '#core/utils/context-utils.js';

import {AbstractFeature, allFeatures} from '#core/application/abstract-feature.js';
import {ApplicationConfig} from '#core/config/application-config.js';
import {DebugFlags} from '#core/application/debug/debugFlags.js';
import {DBXPerformanceService} from '#core/services/performance-reporting-service/index.js';

import {PaletteRetriever} from '#core/application/modules/palette-retriever.js';

import {registerPaletteSchema} from '../schema-registry.js';

import {BasePalette} from './base-palette.js';
import {actions as visualConfigActions} from './visual-config-actions.js';
import {clearTileCache} from './tiles-cache.js';

const STORAGE_CACHE_KEY = 'dbx.visual-features.moj-config-cache';
const STORAGE_CACHE_HASH_KEY = 'dbx.visual-features.hash';

export class AbstractVisualFeature extends AbstractFeature {
    constructor(parentFeature) {
        super(parentFeature);

        VisualFeatures.addFeatureToMapping(this);

        if (parentFeature) {
            if (this.paletteSchema && parentFeature.paletteSchema) {
                this.paletteSchema.parent = parentFeature.paletteSchema.key;
            }
        }

        if (this.paletteSchema) {
            registerPaletteSchema(this.paletteSchema);
        }
    }

    get paletteSchema() {
        return null;
    }

    _buildVisualConfig(result, parent) {
        let palette = null;

        // temp solution
        const {FILL_PAGE} = BasePalette.palette;

        let paletteRetriever;
        if (this.paletteSchema) {
            // construct ready-to-use palette from global config
            paletteRetriever = new PaletteRetriever(this.logger, {
                defaults: {...BasePalette.palette, PARENT: parent?.intermediateCache},
                defaultsResolved: {...BasePalette.palette, PARENT: parent?.resolvedValuesCache},
                basedOn: FILL_PAGE,
            });
            paletteRetriever.doAnalyzeConfigs = DebugFlags.analyzeConfigs;

            // Unfortunately need to access global ApplicationConfig because updateTokens modify global, but not local copies inside features
            palette = paletteRetriever.constructPaletteBySchema(ApplicationConfig.config || {}, this.paletteSchema); // not used yet
        }

        const configs = this.getSerializableVisualConfig(palette);
        mergeTo(result, configs);

        this.paletteSchema && countAndIncreaseAutomatedPropertiesCount(this.paletteSchema.key, configs);

        this.dependants.forEach(
            feature => feature._buildVisualConfig && feature._buildVisualConfig(result, paletteRetriever)
        );
    }

    // eslint-disable-next-line no-unused-vars
    getSerializableVisualConfig(palette) {
        return {};
    }
}

const _featuresMap = [];

class VisualFeatures extends AbstractVisualFeature {
    // eslint-disable-next-line no-unused-vars
    setupConfigs(globalConfig, options, filterFn) {
        BasePalette.load(globalConfig);

        return super.setupConfigs(...arguments);
    }

    updateViewsConfig(viewsConfig) {
        const start = performance.now();

        let visualConfig;
        const startVisual = performance.now();
        const storedConfig = localStorage.getItem(STORAGE_CACHE_KEY);
        const storedHash = localStorage.getItem(STORAGE_CACHE_HASH_KEY);
        const newStoreHash = this.getVisualConfigHash();
        let shouldRebuild = !storedConfig || storedHash !== newStoreHash;

        if (!shouldRebuild) {
            try {
                visualConfig = JSON.parse(storedConfig);
            } catch (e) {
                this.logger.error('VisualFeatures: Error parsing stored visual features config from localStorage', e);
                shouldRebuild = true;
            }
        }
        if (shouldRebuild) {
            visualConfig = this.buildAndCacheConfig(newStoreHash);
        }

        mergeTo(viewsConfig, visualConfig);
        super.updateViewsConfig(viewsConfig);

        performance.measure('dbx:visual-features:visual', {start: startVisual});
        performance.measure('dbx:visual-features', {start});
        DBXPerformanceService.reportSystemMeasurementDuration(
            'dbx:visual-features',
            'dbx.init.visual-features-duration'
        );
    }

    getVisualConfigHash() {
        return `${ApplicationConfig.configHash}/${CLIENT_NAME}/${GIT_HASH}/${isMobile() ? 'M' : 'D'}`;
    }

    buildAndCacheConfig(newStoreHash) {
        let visualConfig = {};
        this._buildVisualConfig(visualConfig);

        newStoreHash = newStoreHash || this.getVisualConfigHash();
        localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(visualConfig));
        localStorage.setItem(STORAGE_CACHE_HASH_KEY, newStoreHash);

        return visualConfig;
    }

    static addFeatureToMapping(feature) {
        if (_featuresMap[feature.name]) {
            this.logger.warn('You are trying to override existing feature in mapping. Feature name: ', feature.name);
        }

        _featuresMap[feature.name] = feature;
    }

    getFeatureByName(name) {
        return _featuresMap[name];
    }
}

const reduxInstance = MojitoCore.Services.redux;
const {configRegistry} = MojitoCore.Services.Config;
reduxInstance.actionListener.startListening({
    actionCreator: visualConfigActions.rebuildAndApplyVisualFromConfig,
    effect: (action, api) => {
        clearTileCache();
        ApplicationConfig.rebuildFromSessionStorage();

        // BasicPalette.load(ApplicationConfig.config);
        allVisuals.setupConfigs(ApplicationConfig.config, ApplicationConfig.initParams);
        const mojConfig = {services: {configurations: {}}};
        allVisuals.updateViewsConfig(mojConfig.services.configurations);
        mergeTo(mojConfig, ApplicationConfig.mojitoConfiguration);
        // const visualConfig = allVisuals.buildAndCacheConfig();
        configRegistry.resetCache();
        configRegistry.addOverrides(mojConfig.services?.configurations || {});
        api.dispatch(visualConfigActions.updateVisualConfigHash(allVisuals.getVisualConfigHash()));
    },
});

export const allVisuals = new VisualFeatures(allFeatures);
